import { BehaviorSubject } from "rxjs";
import { ChartFactory } from "./utils/ChartFactory";
import { SeriesType } from "./utils/SeriesType";
import { BasicChart, SeriesOption, TooltipOption } from "./utils/models";
import { ECElementEvent } from "echarts";

export interface ChartClasses {
    innerContainer: string;
}
export class Chart {
    colors: string[] = ['#0082C6', '#3BB8A3', '#FF5F1F', '#FFC527', '#614BC3'];
    readonly availableChartTypes = [
        { key: SeriesType.Column, icon: 'fa-chart-column', name: 'Column' },
        // { key: SeriesType.Line, icon: 'fa-line-chart', name: 'Line' },
        { key: SeriesType.Bar, icon: 'fa-bar-chart', name: 'Bar' },
        { key: SeriesType.Pie, icon: 'fa-pie-chart', name: 'Pie' },
    ];
    chartTypes = [
        { key: SeriesType.Column, icon: 'fa-chart-column', name: 'Column' },
        // { key: SeriesType.Line, icon: 'fa-line-chart', name: 'Line' },
        { key: SeriesType.Bar, icon: 'fa-bar-chart', name: 'Bar' },
        { key: SeriesType.Pie, icon: 'fa-pie-chart', name: 'Pie' },
    ];

    classes: ChartClasses = {
        innerContainer: "block border border-zen border-opacity-10 rounded-sm p-5"
    }

    chart$ = new BehaviorSubject<BasicChart>({} as BasicChart);
    type: SeriesType = SeriesType.Column;
    title!: string;
    id!: string;
    uniqueId!: string;
    export: boolean = false;
    rawData!: any[] | any;
    categories: string[] = [];
    custom: boolean = false;
    autoCustomChart: boolean = true;
    data: any[] = [];
    onClick?: (params: ECElementEvent) => void;

    get availableToDisplay(): boolean {
        // If series has been populated directly in the chart config (e.g., via setSeries)
        if (this.data?.length)
            return true;
        const series = this.chart$.value?.series as any;
        if (series && Array.isArray(series) && series.length > 0) {
            // Check if any series has actual data items
            const hasSeriesData = series.some((s: any) => s && s.data && Array.isArray(s.data) && s.data.length > 0);
            if (hasSeriesData) {
                return true;
            }
        }
        return false;
    }
    constructor(categories?: string[], data?: any[], type?: SeriesType,) {
        if (!categories || !data)
            return;
        this.categories = categories;
        if (type)
            this.type = type;
        this.data = data;
        this.updateChart();
    }

    init(categories: string[], data: any[], type: SeriesType) {
        this.categories = categories;
        this.type = type;
        this.data = data;
        this.updateChart();
    }

    setTypes(seriesKeys: SeriesType[]) {
        this.chartTypes = this.availableChartTypes.filter(type => seriesKeys?.includes(type?.key));
        return this;
    }

    setColors(colors: string[]) {
        this.colors = colors;
        return this;
    }



    setData(data: any[], categories?: string[]): this {
        const chart = this.chart$.value;
        if (!data.every(x => this.data.includes(x)) || data?.length != this.data?.length) {
            chart.series = this.getDefaultSeries(this.type, data);
        }
        this.data = data;
        if (categories) this.categories = categories;
        return this.updateChart();
    }

    setType(type: SeriesType): this {
        this.type = type;

        if (!this.categories?.length)
            return this;
        const chart = this.chart$.value;
        if ((type == SeriesType.Bar || type == SeriesType.Column) && this.chart$.value?.dataZoom?.length! > 0) {
            if (type == SeriesType.Bar) {
                chart.dataZoom!.map((x: any) => {
                    if (x.type == 'slider')
                        x.yAxisIndex = 0;

                    return x;
                })
            }
            else {
                chart.dataZoom!.map((x: any) => {
                    if (x.type == 'slider')
                        x.xAxisIndex = 0;
                    return x;
                })
            }
            this.chart$.next(chart);
        }
        return this.updateChart(!this.data?.length ? chart.series : null);
    }

    setDisableCustomChart(disable: boolean): this {
        this.autoCustomChart = disable;
        return this;
    }

    setTitle(title: string): this {
        this.title = title;
        return this;
    }

    setGrid(
        top?: number,
        bottom?: number,
        left?: string | number,
        right?: string | number,
        show: boolean = false
    ) {
        const chart = this.chart$.value;
        chart.grid = { top, bottom, left, right, show: true, backgroundColor: '#ccc' };
        this.chart$.next(chart);
        return this;
    }

    setChart(data: any[], categories: string[]) {
        this.data = data;
        this.categories = categories;
        this.updateChart();
        return this;
    }

    setCategorySeries(category: string[], data: SeriesOption[]) {
        this.categories = category;
        this.data = data;
        this.updateChart(data);
        return this;
    }
    setExport(exported: boolean, autoCustomChart: boolean = true): this {
        const chart = this.chart$.value;
        this.autoCustomChart = autoCustomChart;
        this.export = exported;
        this.chart$.next(chart);
        return this;
    }
    setLegend(legend: any[]): this {
        const chart = this.chart$.value;
        chart.legend = { data: legend };
        this.chart$.next(chart);
        return this;
    }

    setTooltip(trigger: string = 'cross', axisPointerType: string = 'shadow') {
        const chart = this.chart$.value;
        chart.tooltip = { trigger, axisPointer: { type: axisPointerType } } as TooltipOption;
        this.chart$.next(chart);
        return this;
    }


    setZoom(inside = true, slider?) {
        const chart = this.chart$.value;
        chart.dataZoom = [];
        chart.dataZoom.push(
            {
                show: inside,
                type: 'inside',
            });
        chart.dataZoom.push(
            {
                show: slider,
                type: 'slider',
            });

        this.chart$.next(chart)

        return this;
    }



    private updateChart(seriesOption?: SeriesOption[]): this {

        if (!(this.id) && this.categories.length)
            this.id = 'chart' + this.categories.slice(0, 4).join('').replace(/[,\-\/`\s]/g, '').toLowerCase() + Math.floor(Math.random() * 1000);
        const series = seriesOption ?? this.getDefaultSeries(this.type, this.data);
        const config = ChartFactory.createChartOptions(this.type, this.categories, series, this.colors);
        const chart = this.chart$.value;
        this.deepMerge(chart, config);
        //console.log(chart);
        this.chart$.next(chart);
        return this;
    }

    setSeries(series: SeriesOption[]) {
        return this.updateChart(series)
    }

    updateOptions(options: Partial<BasicChart>): this {
        const target = this.chart$.value;
        this.deepMerge(target, options);
        this.chart$.next(target);
        console.log(target);

        return this;
    }

    setOptions(options: BasicChart) {
        this.chart$.next(options);
        return this;
    }


    getDefaultSeries(type: SeriesType, series: any[]): SeriesOption[] {
        if (!Array.isArray(series)) {
            throw new Error('Series must be an array');
        }
        const formatter = (params: any) => {
            const val = parseFloat(params.value);
            return val && val !== 0 ? `${val.toFixed(1)}%` : '';
        };

        const wrapValue = (val: any) => ({ value: String(val) });

        // Case: number[] => single unnamed series
        if (typeof series[0] === 'number') {
            return [{
                type,
                data: series.map(wrapValue),
                label: { show: true, position: 'top', formatter }
            }];
        }

        // Case: number[][] => multiple unnamed series
        if (Array.isArray(series[0]) && typeof series[0][0] === 'number') {
            return series.map((data: number[]) => ({
                type,
                data: data.map(wrapValue),
                label: { show: true, position: 'top', formatter }
            }));
        }

        // ✅ Case: array of { category: string, value: string | number, ... }
        if (
            typeof series[0] === 'object' &&
            'category' in series[0] &&
            'value' in series[0] &&
            !('data' in series[0])
        ) {
            return [{
                type,
                data: series.map((item: any) => ({
                    ...item,
                    value: String(item.value)
                })),
                label: { show: true, position: 'top', formatter }
            }];
        }

        // Case: array of { name, data: number[] | object[] }
        if (
            typeof series[0] === 'object' &&
            series.every(item =>
                item &&
                typeof item === 'object' &&
                'data' in item &&
                Array.isArray(item.data)
            )
        ) {
            return series.map((item: { name?: string; data: any[] }) => ({
                type,
                name: item?.name ?? '',
                data: item?.data?.map(entry =>
                    typeof entry === 'object' && 'value' in entry
                        ? { ...entry, value: String(entry.value) }
                        : wrapValue(entry)
                ),
                label: { show: true, position: 'top', formatter }
            }));
        }

        // console.error('Invalid series format:', series);
        return [];
    }


    /**
     * Recursively merges source into target. Arrays and primitives are replaced.
     */
    private deepMerge(target: any, source: any): any {
        if (target.graphic && !source.graphic)
            delete target.graphic;
        for (const key in source) {
            if (
                Object.prototype.hasOwnProperty.call(source, key)
            ) {
                const sourceValue = source[key];
                const targetValue = target[key];

                if (
                    this.isPlainObject(sourceValue) &&
                    this.isPlainObject(targetValue)
                ) {
                    this.deepMerge(targetValue, sourceValue);
                } else {
                    target[key] = sourceValue;
                }
            }
        }
        return target;
    }

    /**
     * Returns true if the value is a plain object (not array, not null).
     */
    private isPlainObject(obj: any): obj is Record<string, any> {
        return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
    }


    setBackgroundImage(imageUrl: string, options?: {
        opacity?: number;
    }): this {
        const chart = this.chart$.value;

        chart.graphic = [
            {
                type: 'image',
                id: 'chart-bg',
                left: 0,
                top: 0,
                bounding: 'raw',
                z: -10,
                style: {
                    image: imageUrl,
                    width: '100%',
                    height: '100%',
                    opacity: options?.opacity ?? 0.2
                }
            }
        ];

        this.chart$.next(chart);
        return this;
    }



    build() {
        if (!(this.id) && this.categories.length)
            this.id = 'chart' + this.categories.slice(0, 4).join('').replace(/[,\-\/`\s]/g, '').toLowerCase();

        if (!this.categories.length)
            throw "Categories need to be provided, using build() requires categories";
        this.chart$.next(this.chart$.value);
        return this;
    }

}